import { Stack, Table, TableBody, TableCell, TableHead, TableRow, type Theme, Typography } from '@mui/material';
import useUnitProvider from '/@/core/context/unit';
import { _ } from '/@/locale';
import type { DensitySimulationSnapshot, SimulationRow, SimulationRowKey } from './density-simulation-data';

/** No theme token behind the design's habitual-density column. */
export const HABITUAL_BACKGROUND = '#F6ECFF';
const NOT_AVAILABLE = '-';

const ROW_LABELS: Record<SimulationRowKey, () => string> = {
    over: () => _('Sobrestimado'),
    estimated: () => _('Estimado'),
    under: () => _('Subestimado'),
};

const differenceColor = (value?: number) => (theme: Theme) =>
    value === undefined || value === 0
        ? theme.palette.primary.main
        : value > 0
          ? theme.palette.success.main
          : theme.palette.warning.main;

const recommendedCell = { backgroundColor: (theme: Theme) => theme.palette.primary.infoBackground };
const habitualCell = { backgroundColor: HABITUAL_BACKGROUND };

/**
 * "Rendimientos según densidad y ambiente". Pure presentation over a precomputed snapshot, so
 * the block-image render (`v2/block-image`) draws the same table the page shows.
 */
function DensitySimulationTable({
    recommendedDensity,
    habitualDensity,
    betterPercent,
    worsePercent,
    rows,
    recommendedDifferences,
    habitualDifferences,
}: DensitySimulationSnapshot) {
    const units = useUnitProvider();

    const formatDensity = (value?: number) =>
        value !== undefined ? units.toCornDensity(value, undefined).toString() : NOT_AVAILABLE;
    const formatYield = (value?: number) =>
        value !== undefined ? units.toCornYield(value, undefined).toString() : NOT_AVAILABLE;
    /** Differences read as whole kg/ha, which is how the design quotes them. */
    const formatDifference = (value?: number) => {
        if (value === undefined) return '';
        const converted = units.toCornYield(value, 0);
        return `${converted.value > 0 ? '+' : ''}${converted.toString()}`;
    };

    const betterLabel = `+${betterPercent}%`;
    const worseLabel = `-${worsePercent}%`;
    const rowCaption = (row: SimulationRow) =>
        row.key === 'estimated'
            ? formatYield(row.environment)
            : `${formatYield(row.environment)} (${row.key === 'over' ? betterLabel : worseLabel})`;

    return (
        <Table
            size='small'
            sx={{
                border: '1px solid',
                borderColor: (theme) => theme.palette.divider,
                tableLayout: 'fixed',
                '& td, & th': { px: 1.5, py: 1, verticalAlign: 'middle' },
            }}
        >
            <TableHead>
                <TableRow>
                    <TableCell sx={{ borderBottom: 'none' }} />
                    <TableCell
                        colSpan={3}
                        sx={{ backgroundColor: (theme) => theme.palette.grey[100], borderBottom: 'none' }}
                    >
                        <Typography variant='body2' fontWeight={700} color='primary'>
                            {_('Rendimientos según densidad y ambiente')}
                        </Typography>
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell>
                        <Typography variant='caption' fontWeight={700} color='primary'>
                            {_('Ambiente')}
                        </Typography>
                    </TableCell>
                    <TableCell sx={recommendedCell}>
                        <Typography variant='body2' fontWeight={700} color='primary'>
                            {_('Densidad recomendada')}
                        </Typography>
                        <Typography variant='body2' fontWeight={700} color='primary'>
                            {formatDensity(recommendedDensity)}
                        </Typography>
                    </TableCell>
                    <TableCell sx={habitualCell}>
                        <Typography variant='caption' component='p' fontWeight={700} color='primary'>
                            {_('Densidad habitual')}
                        </Typography>
                        <Typography variant='caption' component='p' color='primary.mui.inactive'>
                            {formatDensity(habitualDensity)}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography variant='caption' fontWeight={700} color='primary'>
                            {_('Diferencial por densidad')}
                        </Typography>
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row) => {
                    const isEstimated = row.key === 'estimated';
                    return (
                        <TableRow key={row.key}>
                            <TableCell>
                                <Typography variant='body2' fontWeight={isEstimated ? 700 : 400} color='primary'>
                                    {ROW_LABELS[row.key]()}
                                </Typography>
                                <Typography
                                    variant={isEstimated ? 'body2' : 'caption'}
                                    component='p'
                                    fontWeight={isEstimated ? 700 : 400}
                                    color={isEstimated ? 'primary' : 'primary.mui.inactive'}
                                >
                                    {rowCaption(row)}
                                </Typography>
                            </TableCell>
                            <TableCell sx={recommendedCell}>
                                <Typography
                                    variant={isEstimated ? 'subtitle1' : 'body2'}
                                    fontWeight={isEstimated ? 700 : 400}
                                    color='primary'
                                >
                                    {formatYield(row.recommendedYield)}
                                </Typography>
                            </TableCell>
                            <TableCell sx={habitualCell}>
                                <Typography variant='body2' color='primary'>
                                    {formatYield(row.habitualYield)}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography
                                    variant='body2'
                                    fontWeight={700}
                                    sx={{ color: differenceColor(row.densityDifferential) }}
                                >
                                    {formatDifference(row.densityDifferential)}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    );
                })}
                <TableRow>
                    <TableCell>
                        <Stack direction='row' justifyContent='space-between' alignItems='center' gap={1}>
                            <Typography variant='caption' fontWeight={700} color='primary'>
                                {_('Diferencia por ambiente')}
                            </Typography>
                            <Stack>
                                <Typography variant='caption' color='primary.mui.inactive'>
                                    {betterLabel}
                                </Typography>
                                <Typography variant='caption' color='primary.mui.inactive'>
                                    {worseLabel}
                                </Typography>
                            </Stack>
                        </Stack>
                    </TableCell>
                    {[
                        { key: 'recommended', differences: recommendedDifferences, sx: recommendedCell },
                        { key: 'habitual', differences: habitualDifferences, sx: habitualCell },
                    ].map(({ key, differences, sx }) => (
                        <TableCell key={key} sx={sx}>
                            {[
                                { key: 'up', value: differences.up },
                                { key: 'down', value: differences.down },
                            ].map(({ key: direction, value }) => (
                                <Typography
                                    key={direction}
                                    variant='body2'
                                    fontWeight={700}
                                    sx={{ color: differenceColor(value) }}
                                >
                                    {formatDifference(value)}
                                </Typography>
                            ))}
                        </TableCell>
                    ))}
                    <TableCell />
                </TableRow>
            </TableBody>
        </Table>
    );
}

export default DensitySimulationTable;
